import { ErrorHandler, Injectable } from '@angular/core';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (this.isLikelyChunkLoadFailure(error)) {
      this.tryRecoverFromChunkLoadFailure();
      return;
    }
  }

  private isLikelyChunkLoadFailure(error: unknown): boolean {
    const message = this.extractErrorMessage(error);
    if (!message) return false;

    return (
      message.includes('ChunkLoadError') ||
      /Loading chunk \d+ failed/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message) ||
      /Importing a module script failed/i.test(message)
    );
  }

  private extractErrorMessage(error: unknown): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message || String(error);

    try {
      // Some bundlers throw plain objects with message fields.
      const maybeMessage = (error as { message?: unknown }).message;
      return typeof maybeMessage === 'string' ? maybeMessage : String(error);
    } catch {
      return '';
    }
  }

  private tryRecoverFromChunkLoadFailure(): void {
    // Avoid infinite reload loops if the failure is persistent (e.g., outage).
    const guardKey = 'elx_chunk_recovery_attempted';
    if (sessionStorage.getItem(guardKey) === '1') {
      return;
    }
    sessionStorage.setItem(guardKey, '1');

    // Best-effort: clear Cache Storage (if present), then reload with a cache buster.
    // This targets the common case where index.html is updated but the browser/CDN
    // is still serving old chunk URLs, leading to a blank white page.
    const reload = () => {
      const url = new URL(window.location.href);
      url.searchParams.set('reload', String(Date.now()));
      window.location.replace(url.toString());
    };

    try {
      if ('caches' in window) {
        // Fire-and-forget; don't block the reload.
        void caches
          .keys()
          .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
          .finally(reload);
        return;
      }
    } catch {
      // ignore and reload below
    }

    reload();
  }
}

